/** 臨時ノード（不時着・ビバークの地点。docs/03_simulation.md 2 章） */
import { SNAP_TO_NODE_M } from '../../config/balance';
import { Biome, type EdgeData, type EdgeSegment, type NodeData } from '../../core/world';
import { addEdge, addNode, type Graph } from './graph';
import type { TerrainSampler } from './terrain';

const STEP_M = 80;
const MAX_LINK_M = 8000;

/** 2 点を直線で結ぶ経路（湖を横切るなら null） */
function straightEdge(graph: Graph, terrain: TerrainSampler, a: NodeData, b: NodeData): EdgeData | null {
  const len = Math.hypot(b.pos.x - a.pos.x, b.pos.z - a.pos.z);
  const n = Math.max(1, Math.ceil(len / STEP_M));
  const segments: EdgeSegment[] = [];
  const polyline = new Float32Array((n + 1) * 3);
  let ascentM = 0;
  let descentM = 0;
  let prevH = terrain.heightAt(a.pos.x, a.pos.z);
  polyline.set([a.pos.x, prevH, a.pos.z], 0);
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    const x = a.pos.x + (b.pos.x - a.pos.x) * t;
    const z = a.pos.z + (b.pos.z - a.pos.z) * t;
    const h = terrain.heightAt(x, z);
    const biome = terrain.biomeAt(x - ((b.pos.x - a.pos.x) / n) * 0.5, z - ((b.pos.z - a.pos.z) / n) * 0.5);
    if (biome === Biome.Lake || biome === Biome.Cliff) return null;
    const segLen = len / n;
    segments.push({ lengthM: segLen, grade: segLen > 0 ? (h - prevH) / segLen : 0, biome });
    if (h > prevH) ascentM += h - prevH;
    else descentM += prevH - h;
    polyline.set([x, h, z], i * 3);
    prevH = h;
  }
  return { id: graph.edges.length, a: a.id, b: b.id, lengthM: len, ascentM, descentM, segments, polyline };
}

/**
 * 地点 (x, z) にノードを用意する。近くに既存のノードがあればそれを使う。
 * prefer を渡すと、そのノードたちに優先してつなぐ（ビバークで経路の途中に止まったとき）。
 */
export function ensureNodeAt(
  graph: Graph,
  terrain: TerrainSampler,
  x: number,
  z: number,
  prefer: number[] = [],
): { nodeId: number; created: boolean } {
  let nearest: NodeData | null = null;
  let nearestD = Infinity;
  for (const n of graph.nodes) {
    const d = Math.hypot(n.pos.x - x, n.pos.z - z);
    if (d < nearestD) {
      nearestD = d;
      nearest = n;
    }
  }
  if (nearest && nearestD <= SNAP_TO_NODE_M) return { nodeId: nearest.id, created: false };

  const node: NodeData = { id: graph.nodes.length, kind: 'temp', pos: { x, y: terrain.heightAt(x, z), z } };
  addNode(graph, node);

  const candidates = [
    ...prefer.map((id) => graph.nodes[id] as NodeData),
    ...graph.nodes
      .filter((n) => n.id !== node.id && !prefer.includes(n.id))
      .sort((p, q) => Math.hypot(p.pos.x - x, p.pos.z - z) - Math.hypot(q.pos.x - x, q.pos.z - z))
      .slice(0, 8),
  ];
  let linked = 0;
  for (const c of candidates) {
    if (linked >= 2) break;
    if (linked > 0 && !prefer.includes(c.id) && Math.hypot(c.pos.x - x, c.pos.z - z) > MAX_LINK_M) break;
    const e = straightEdge(graph, terrain, node, c);
    if (!e) continue;
    addEdge(graph, e);
    linked++;
  }
  return { nodeId: node.id, created: true };
}
